import { useEffect, useRef, useState } from 'react';
import {
  AlphaSlider,
  Button,
  ColorArea,
  ColorPicker,
  ColorSlider,
  Dropdown,
  Input,
  Option,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  mergeClasses,
  tokens,
  type ColorPickerProps,
} from '@fluentui/react-components';
import {
  CSS_NAMED_COLORS,
  HEX_DRAFT_REGEX,
  hsvToHex,
  hsvToRgb,
  namedColorForHex,
  normalizeHex,
  toHexOrEmpty,
  toHsv,
  type HsvColor,
} from '../utils/color';

const useStyles = makeStyles({
  controls: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    width: '100%',
  },
  namedDropdown: {
    minWidth: '150px',
  },
  separator: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  hexGroup: {
    display: 'flex',
    alignItems: 'stretch',
    width: '160px',
  },
  hexField: {
    flex: 1,
    minWidth: 0,
    borderTopRightRadius: '0 !important',
    borderBottomRightRadius: '0 !important',
    '::after': {
      display: 'none !important',
    },
  },
  swatchWrap: {
    display: 'flex',
    flexShrink: 0,
  },
  swatch: {
    boxSizing: 'border-box',
    width: '32px',
    minWidth: '32px',
    height: '100%',
    margin: 0,
    padding: 0,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    borderLeft: 0,
    borderRadius: `0 ${tokens.borderRadiusMedium} ${tokens.borderRadiusMedium} 0`,
    cursor: 'pointer',
    appearance: 'none',
    boxShadow: 'none',
  },
  picker: {
    width: '300px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  row: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    width: '100%',
  },
  sliders: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    boxSizing: 'border-box',
  },
  slider: {
    width: '100% !important',
    minWidth: '0px !important',
  },
  alphaSlider: {
    width: '100%',
    minWidth: '0px',
    '& .fui-AlphaSlider__rail': {
      backgroundImage: `linear-gradient(to right, transparent, var(--fui-AlphaSlider__rail--color)), repeating-conic-gradient(${tokens.colorNeutralStroke2} 0% 25%, ${tokens.colorNeutralBackground1} 0% 50%)`,
      backgroundSize: 'auto, 8px 8px',
    },
  },
  preview: {
    width: '50px',
    height: '50px',
    borderRadius: tokens.borderRadiusMedium,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    flexShrink: 0,
    '@media (forced-colors: active)': {
      forcedColorAdjust: 'none',
    },
  },
  optionSwatch: {
    width: '16px',
    height: '16px',
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusSmall,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

const HexInput = ({
  value,
  onValid,
  disabled,
  className,
}: {
  value: string;
  onValid: (hex: string) => void;
  disabled?: boolean;
  className?: string;
}) => {
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setDraft(value);
  }, [value]);

  return (
    <Input
      aria-label="Hex color"
      placeholder="#RRGGBB"
      className={className}
      disabled={disabled}
      value={draft}
      onFocus={() => {
        focused.current = true;
      }}
      onBlur={() => {
        focused.current = false;
        setDraft(value);
      }}
      onChange={(_, data) => {
        if (!HEX_DRAFT_REGEX.test(data.value)) return;
        setDraft(data.value);
        const hex = normalizeHex(data.value);
        if (hex) onValid(hex);
      }}
    />
  );
};

export interface ColorFieldProps {
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
}

export const ColorField = ({ value, onChange, disabled }: ColorFieldProps) => {
  const styles = useStyles();
  const [draft, setDraft] = useState<HsvColor | null>(null);
  const selectedName = namedColorForHex(value);
  const swatchColor = toHexOrEmpty(value);

  const changeDraft: ColorPickerProps['onColorChange'] = (_, data) =>
    setDraft({ ...data.color, a: data.color.a ?? 1 });

  return (
    <div className={styles.controls}>
      <Dropdown
        placeholder="Select a color"
        className={styles.namedDropdown}
        disabled={disabled}
        value={selectedName ?? ''}
        selectedOptions={selectedName ? [selectedName] : []}
        onOptionSelect={(_, data) => {
          if (data.optionValue) onChange(toHexOrEmpty(data.optionValue));
        }}
      >
        {CSS_NAMED_COLORS.map((colorName) => (
          <Option key={colorName} value={colorName} text={colorName}>
            <div className={styles.option}>
              <div
                className={styles.optionSwatch}
                style={{ backgroundColor: colorName }}
              />
              {colorName}
            </div>
          </Option>
        ))}
      </Dropdown>
      <span className={styles.separator}>or</span>
      <div className={styles.hexGroup}>
        <HexInput
          value={value}
          onValid={onChange}
          disabled={disabled}
          className={styles.hexField}
        />
        <span className={styles.swatchWrap}>
          <Popover
            open={draft !== null}
            trapFocus
            onOpenChange={(_, data) =>
              setDraft(data.open ? toHsv(value) : null)
            }
          >
            <PopoverTrigger disableButtonEnhancement>
              <button
                type="button"
                className={styles.swatch}
                style={{
                  backgroundColor:
                    swatchColor || tokens.colorNeutralBackground1,
                }}
                aria-label="Custom color picker"
                disabled={disabled}
              />
            </PopoverTrigger>
            <PopoverSurface>
              {draft && (
                <div className={styles.picker}>
                  <ColorPicker color={draft} onColorChange={changeDraft}>
                    <ColorArea
                      inputX={{ 'aria-label': 'Saturation' }}
                      inputY={{ 'aria-label': 'Brightness' }}
                    />
                    <div className={styles.row}>
                      <div className={styles.sliders}>
                        <ColorSlider
                          aria-label="Hue"
                          className={styles.slider}
                        />
                        <AlphaSlider
                          aria-label="Alpha"
                          className={mergeClasses(
                            styles.slider,
                            styles.alphaSlider
                          )}
                          style={{
                            ['--fui-AlphaSlider__rail--color' as string]:
                              hsvToHex(draft),
                            ['--fui-AlphaSlider--direction' as string]:
                              'to right',
                          }}
                        />
                      </div>
                      <div
                        className={styles.preview}
                        style={{ backgroundColor: hsvToRgb(draft) }}
                      />
                    </div>
                  </ColorPicker>
                  <HexInput
                    value={hsvToHex(draft)}
                    onValid={(hex) => setDraft(toHsv(hex))}
                  />
                  <div className={styles.row}>
                    <Button
                      appearance="primary"
                      onClick={() => {
                        onChange(hsvToHex(draft));
                        setDraft(null);
                      }}
                    >
                      Ok
                    </Button>
                    <Button onClick={() => setDraft(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </PopoverSurface>
          </Popover>
        </span>
      </div>
    </div>
  );
};

export default ColorField;
